import { observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IPurchasedItemEntityAttributes extends IModelAttributes {
	purchasePrice: number;
	pointOfSaleProductId: string;
	pointOfSaleProduct?: Models.IPointOfSaleProductEntityAttributes;
	userId: string | null;
	user?: Models.IUserEntityAttributes;
	transaction?: Models.ITransactionEntityAttributes;
}

@entity('PurchasedItemEntity', 'PurchasedItem')
export default class PurchasedItemEntity extends Model implements IPurchasedItemEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<PurchasedItemEntity, number>()
	public purchasePrice: number;

	@observable
	@attribute()
	public pointOfSaleProductId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public pointOfSaleProduct: Models.PointOfSaleProductEntity;

	@observable
	@attribute()
	public userId: string | null;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction: Models.TransactionEntity;

	constructor(attributes?: Partial<IPurchasedItemEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPurchasedItemEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.purchasePrice !== undefined) {
				this.purchasePrice = attributes.purchasePrice;
			}
			if (attributes.pointOfSaleProductId !== undefined) {
				this.pointOfSaleProductId = attributes.pointOfSaleProductId;
			}
			if (attributes.pointOfSaleProduct !== undefined) {
				if (attributes.pointOfSaleProduct === null) {
					this.pointOfSaleProduct = attributes.pointOfSaleProduct;
				} else if (attributes.pointOfSaleProduct instanceof Models.PointOfSaleProductEntity) {
					this.pointOfSaleProduct = attributes.pointOfSaleProduct;
					this.pointOfSaleProductId = attributes.pointOfSaleProduct.id;
				} else {
					this.pointOfSaleProduct = new Models.PointOfSaleProductEntity(attributes.pointOfSaleProduct);
					this.pointOfSaleProductId = this.pointOfSaleProduct.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.transaction !== undefined) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
				}
			}
		}
	}
}
